// CoordinateHUD.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { useMap, useMapEvents } from "react-leaflet";

/** ----- helpers (컴포넌트 밖: 의존성 경고 예방) ----- */
function toDMS(val, isLat) {
  const dir = isLat ? (val >= 0 ? "N" : "S") : (val >= 0 ? "E" : "W");
  const abs = Math.abs(val);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = (minFloat - min) * 60;
  return `${deg}° ${min}' ${sec.toFixed(2)}" ${dir}`;
}

function formatLatLng(latlng, format, digits) {
  if (!latlng) return "-";
  const { lat, lng } = latlng;
  if (format === "dms") {
    return `${toDMS(lat, true)}  •  ${toDMS(lng, false)}`;
  }
  return `${lat.toFixed(digits)}, ${lng.toFixed(digits)}`;
}

/** ----- HUD 컴포넌트 ----- */
export default function CoordinateHUD({
  position = "bottomright",  // Leaflet control 위치
  format = "dec",            // "dec" | "dms"
  digits = 6,
  showCenter = true,
  showZoom = true,
  className = "",
}) {
  const map = useMap();

  // 실시간 상태
  const [cursor, setCursor] = useState(null);
  const [center, setCenter] = useState(map.getCenter());
  const [zoom, setZoom] = useState(map.getZoom());

  // 지도 이벤트 구독 (커서/센터/줌 업데이트)
  useMapEvents({
    mousemove(e) {
      setCursor(e.latlng);
    },
    move() {
      setCenter(map.getCenter());
    },
    zoom() {
      setZoom(map.getZoom());
    },
  });

  // Leaflet Control 생성/제거
  const divRef = useRef(null);
  useEffect(() => {
    const ctrl = L.control({ position });
    ctrl.onAdd = () => {
      const div = L.DomUtil.create("div", `leaflet-bar leaflet-control ${className}`);
      Object.assign(div.style, {
        background: "rgba(255,255,255,0.9)",
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: "8px",
        padding: "8px 10px",
        fontSize: "12px",
        lineHeight: "1.25",
        maxWidth: "320px",
      });
      L.DomEvent.disableClickPropagation(div);
      L.DomEvent.disableScrollPropagation(div);
      divRef.current = div;
      return div;
    };
    ctrl.addTo(map);
    return () => ctrl.remove();
  }, [map, position, className]);


  //${showCenter ? `<div><span style="opacity:.7;">중심:</span> ${cen}</div>` : ""}

  // HUD HTML 생성: 의존성 모두 useMemo로 관리
  const hudHtml = useMemo(() => {
    const cur = formatLatLng(cursor, format, digits);
    //const cen = formatLatLng(center, format, digits);
    return `
      <div style="text-align:left;font-weight:600;margin-bottom:4px;">좌표 정보</div>
      <div style="text-align:left;"><span style="opacity:.7;">커서:</span> ${cur}</div>
      ${showZoom ? `<div style="text-align:left;"><span style="opacity:.7;">줌:</span> ${zoom}</div>` : ""}
    `;
  }, [cursor, zoom, format, digits, showZoom]);

  // HUD DOM 반영: 문자열만 의존 → 의존성 경고 없음
  useEffect(() => {
    if (divRef.current) {
      divRef.current.innerHTML = hudHtml;
    }
  }, [hudHtml]);

  return null; // DOM은 Leaflet control이 관리
}